import { Box } from "fds";
import React, { useState, useEffect, useRef } from "react";

interface Member {
  userId: string;
  userName: string;
  userType?: number;
}

interface Message {
  text: string;
  at: number;
  mine: boolean;
  memberIdx?: number;
}

const REPLIES = [
  "Got it, thanks!",
  "Sure, I'll take a look.",
  "Noted. I'll follow up soon.",
  "Thanks for sharing!",
  "Sounds good to me.",
  "Will do — appreciate the update.",
  "Let me check and get back to you.",
];

function buildSeedMessages(): Message[] {
  const now = Date.now();
  const day = 86400000;
  return [
    {
      text: "Hey, I just shared the updated design spec. Can you take a look?",
      at: now - 3 * day + 2 * 3600000,
      mine: false,
      memberIdx: 1,
    },
    {
      text: "Sure, I'll check it now.",
      at: now - 3 * day + 2 * 3600000 + 120000,
      mine: true,
    },
    {
      text: "The component hierarchy has changed — let me know if you have questions.",
      at: now - 3 * day + 3 * 3600000,
      mine: false,
      memberIdx: 1,
    },
    {
      text: "Got it. Looks cleaner. I'll leave comments directly in the doc.",
      at: now - 3 * day + 4 * 3600000,
      mine: true,
    },
    {
      text: "I uploaded the icon set as well. It's in the same folder.",
      at: now - 3 * day + 5 * 3600000,
      mine: false,
      memberIdx: 2,
    },
    {
      text: "Perfect, I'll review both together.",
      at: now - 3 * day + 5 * 3600000 + 90000,
      mine: true,
    },
    {
      text: "API spec v2 is ready. Please review before sprint planning tomorrow.",
      at: now - 2 * day + 2 * 3600000,
      mine: false,
      memberIdx: 0,
    },
    {
      text: "Will do. Is the auth endpoint finalized?",
      at: now - 2 * day + 2 * 3600000 + 420000,
      mine: true,
    },
    {
      text: "Yes, we switched to OAuth 2.0. Details are in section 3.",
      at: now - 2 * day + 2 * 3600000 + 600000,
      mine: false,
      memberIdx: 0,
    },
    {
      text: "Thanks, I'll review section 3 first.",
      at: now - 2 * day + 2 * 3600000 + 720000,
      mine: true,
    },
    {
      text: "Tomorrow's meeting has been moved to 3 PM.",
      at: now - 2 * day + 9 * 3600000,
      mine: false,
      memberIdx: 1,
    },
    {
      text: "Updated, thanks!",
      at: now - 2 * day + 9 * 3600000 + 180000,
      mine: true,
    },
    {
      text: "Campaign materials are ready. Please check the shared document.",
      at: now - day + 0 * 3600000,
      mine: false,
      memberIdx: 3,
    },
    {
      text: "I'll take a look right now.",
      at: now - day + 0 * 3600000 + 240000,
      mine: true,
    },
    {
      text: "Feedback needed by EOD — launch is scheduled for next Monday.",
      at: now - day + 0 * 3600000 + 360000,
      mine: false,
      memberIdx: 3,
    },
    {
      text: "Understood. I'll send my comments before 5 PM.",
      at: now - day + 0 * 3600000 + 540000,
      mine: true,
    },
    {
      text: "Design system component update is complete.",
      at: now - 1 * 3600000,
      mine: false,
      memberIdx: 1,
    },
    {
      text: "Great work! I'll sync with the frontend team to get this integrated.",
      at: now - 50 * 60000,
      mine: true,
    },
    {
      text: "Sharing the security audit schedule for your reference.",
      at: now - 25 * 60000,
      mine: false,
      memberIdx: 2,
    },
    {
      text: "On it. I'll confirm my availability.",
      at: now - 10 * 60000,
      mine: true,
    },
  ];
}

function fmtTime(ms: number): string {
  const d = new Date(ms);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
}

function dateDivider(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLOR = "#d9e7ff";

const DemoChat = ({ members }: { members: Member[] }) => {
  const [messages, setMessages] = useState<Message[]>(() =>
    buildSeedMessages()
  );
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMessages((prev) => [...prev, { text, at: Date.now(), mine: true }]);

    if (members.length > 0) {
      const replyIdx = Math.floor(Math.random() * REPLIES.length);
      const memberIdx = Math.floor(Math.random() * members.length);
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { text: REPLIES[replyIdx], at: Date.now(), mine: false, memberIdx },
        ]);
      }, 800 + Math.random() * 700);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  let lastDateStr: string | null = null;

  return (
    <Box
      direction="column"
      height={"100%"}
      width={"100%"}
      style={{
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        style={{ flex: 1, overflowY: "auto", padding: "16px 16px 24px" }}
      >
        <Box width={"100%"} direction="column" style={{ gap: 4 }}>
          {messages.map((msg, i) => {
            const dateStr = dateDivider(msg.at);
            const showDivider = dateStr !== lastDateStr;
            lastDateStr = dateStr;
            const member =
              msg.memberIdx !== undefined
                ? members[msg.memberIdx % Math.max(members.length, 1)]
                : undefined;

            return (
              <React.Fragment key={i}>
                {showDivider && (
                  <Box
                    style={{
                      alignItems: "center",
                      gap: 10,
                      margin: "12px 0 8px",
                      color: "#999",
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{ flex: 1, height: 1, background: "#e0e0e0" }}
                    />
                    {dateStr}
                    <span
                      style={{ flex: 1, height: 1, background: "#e0e0e0" }}
                    />
                  </Box>
                )}
                {msg.mine ? (
                  <Box
                    style={{
                      alignItems: "flex-end",
                      gap: 8,
                      maxWidth: "75%",
                      marginLeft: "auto",
                      marginBottom: 2,
                      flexDirection: "row-reverse",
                    }}
                  >
                    <Box
                      direction="column"
                      style={{
                        gap: 2,
                        alignItems: "flex-end",
                      }}
                    >
                      <Box
                        style={{
                          alignItems: "flex-end",
                          gap: 4,
                          flexDirection: "row-reverse",
                        }}
                      >
                        <Box
                          style={{
                            background: "#3182f6",
                            borderRadius: 12,
                            padding: "8px 12px",
                            fontSize: 13,
                            color: "#fff",
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.text}
                        </Box>
                        <span
                          style={{
                            fontSize: 10,
                            color: "#bbb",
                            whiteSpace: "nowrap",
                            paddingBottom: 2,
                          }}
                        >
                          {fmtTime(msg.at)}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    style={{
                      alignItems: "flex-end",
                      gap: 8,
                      maxWidth: "75%",
                      marginBottom: 2,
                    }}
                  >
                    <Box
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",

                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#2f6ae9",
                        flexShrink: 0,
                        background: AVATAR_COLOR,
                        border: "1px solid rgba(47,106,233,0.1)",
                      }}
                    >
                      {member
                        ? initials(member.userName || member.userId)
                        : "?"}
                    </Box>
                    <Box
                      direction="column"
                      style={{
                        gap: 2,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#888",
                          padding: "0 4px",
                        }}
                      >
                        {member?.userName || member?.userId || "Member"}
                      </span>
                      <Box
                        style={{
                          alignItems: "flex-end",
                          gap: 4,
                        }}
                      >
                        <Box
                          style={{
                            background: "#f5f6f7",
                            borderRadius: 12,
                            padding: "8px 12px",
                            fontSize: 13,
                            color: "#222",
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.text}
                        </Box>
                        <span
                          style={{
                            fontSize: 10,
                            color: "#bbb",
                            whiteSpace: "nowrap",
                            paddingBottom: 2,
                          }}
                        >
                          {fmtTime(msg.at)}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Box
        style={{
          borderTop: "1px solid #e0e0e0",
          background: "#fff",
          padding: "10px 14px",

          alignItems: "center",
          gap: 8,
        }}
        classes="mg mt-8"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          style={{
            flex: 1,
            border: "1px solid #ddd",
            borderRadius: 20,
            padding: "7px 14px",
            fontSize: 13,
            outline: "none",
            background: "#fafafa",
            color: "#333",
          }}
        />
        <button
          onClick={handleSend}
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "#5b8dee",
            border: "none",
            cursor: "pointer",

            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
            <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
          </svg>
        </button>
      </Box>
    </Box>
  );
};

export default DemoChat;
